import { Injectable, inject, effect } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { createTracker, httpSink, Tracker, BreadcrumbEvent } from '@lfreiredev/eazycrumb';

const SESSION_KEY = 'eazycrumb_session_id';

@Injectable({ providedIn: 'root' })
export class BreadcrumbTrackerService {
  private readonly authService = inject(AuthService);
  private tracker: Tracker | null = null;

  constructor() {
    if (typeof window === 'undefined') return;
    this.tracker = this.initTracker();

    effect(() => {
      const user = this.authService.currentUser();
      const role = user?.role ? String(user.role) : 'ANON';
      this.tracker?.setContext({ role });
    });
  }

  private initTracker(): Tracker {
    const sessionId = this.getSessionId();
    const endpoint = `${environment.apiUrl}/breadcrumbs/ingest`;

    const tracker = createTracker({
      maxBreadcrumbs: 100,
      enrich: (event) => {
        const data = isRecord(event.data) ? { ...event.data } : {};
        if (!data['path']) {
          data['path'] = safePath(window.location.href);
        }
        return { ...event, data };
      },
      redact: (event) => sanitizeEvent(event),
      sinks: [
        httpSink({
          endpoint,
          flushIntervalMs: 30000,
          maxBatchSize: 50,
          sessionId,
          getContext: () => ({ role: this.authService.currentUser()?.role || 'ANON' }),
        }),
      ],
    });

    tracker.setContext({
      sessionId,
      role: this.authService.currentUser()?.role || 'ANON',
      environment: environment.production ? 'prod' : 'dev',
    });

    tracker.attachDOM({
      clicks: true,
      navigation: true,
      network: true,
      errors: true,
    });

    return tracker;
  }

  private getSessionId(): string {
    try {
      const existing = sessionStorage.getItem(SESSION_KEY);
      if (existing) return existing;
      const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `sess_${Math.random().toString(36).slice(2, 12)}`;
      sessionStorage.setItem(SESSION_KEY, id);
      return id;
    } catch {
      return `sess_${Math.random().toString(36).slice(2, 12)}`;
    }
  }
}

function sanitizeEvent(event: BreadcrumbEvent): BreadcrumbEvent {
  const data = isRecord(event.data) ? { ...event.data } : {};

  if (data['type'] === 'click') {
    if (typeof data['selector'] === 'string') {
      event = { ...event, message: `Click [${data['selector']}]` };
    } else {
      event = { ...event, message: 'Click' };
    }
  } else if (data['type'] === 'navigation' && typeof data['from'] === 'string' && typeof data['to'] === 'string') {
    const from = safePath(data['from']);
    const to = safePath(data['to']);
    event = { ...event, message: `Navigation ${from} → ${to}` };
  } else if (typeof data['type'] === 'string' && data['type'].startsWith('net.') && typeof data['url'] === 'string') {
    const url = safePath(data['url']);
    event = { ...event, message: `${data['type']} ${url}` };
  } else {
    event = { ...event, message: sanitizeText(event.message) };
  }

  stripKeys(data, [
    'text',
    'value',
    'placeholder',
    'content',
    'description',
    'title',
    'name',
    'email',
    'company',
    'legalBasis',
    'supportingDocs',
    'requestBody',
    'responseBody',
    'headers',
  ]);

  if (isRecord(data['error'])) {
    const error = data['error'] as Record<string, unknown>;
    stripKeys(error, ['stack']);
    if (typeof error['message'] === 'string') {
      error['message'] = sanitizeText(error['message']);
    }
  }

  if (typeof data['url'] === 'string') data['url'] = safePath(data['url']);
  if (typeof data['from'] === 'string') data['from'] = safePath(data['from']);
  if (typeof data['to'] === 'string') data['to'] = safePath(data['to']);
  if (typeof data['path'] === 'string') data['path'] = safePath(data['path']);

  stripKeys(data, ['context', 'user']);

  return { ...event, data };
}

function stripKeys(target: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    if (key in target) {
      delete target[key];
    }
  }
}

function sanitizeText(value: string): string {
  const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
  return value.replace(emailRegex, '[redacted]');
}

function safePath(input: string): string {
  try {
    const url = new URL(input, window.location.origin);
    return url.pathname;
  } catch {
    return input.split('?')[0].split('#')[0];
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
